import React, { memo, useContext, useCallback, useState, useRef, useEffect } from 'react';
import type { NodeProps } from 'reactflow';
import { GraphViewContext } from '../GraphView';
import { BaseNode } from './BaseNode';
import type { SimpleCondition } from '../../../../types/policy';

/**
 * Type-safe helper to get operator value from simple condition
 */
function getConditionValue(condition: SimpleCondition | null | undefined, operator: string): unknown {
  if (!condition || typeof condition !== 'object') return '';
  if (operator in condition) {
    return (condition as unknown as Record<string, unknown>)[operator];
  }
  return '';
}

interface ActionButtonProps {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  label: string;
  tooltip: string;
  variant?: 'default' | 'danger';
}
const ActionButton: React.FC<ActionButtonProps> = ({ onClick, label, tooltip, variant = 'default' }) => (
  <button
    onClick={onClick}
    title={tooltip}
    className={
      `p-2 text-xs rounded transition-colors duration-150
      ${variant === 'danger'
        ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900'
        : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}`
    }
  >
    {label}
  </button>
);

export const ConditionNode: React.FC<NodeProps> = memo(({ data, selected }) => {
  const handlers = useContext(GraphViewContext);
  if (!handlers) {
    console.error('[ConditionNode] Missing GraphViewContext');
    return null;
  }

  const path = data.path ?? [];

  const handleEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    handlers.onEditNode(path);
  }, [handlers, path]);
  const handleDelete = useCallback(() => handlers.handleDeleteNode(path), [handlers, path]);
  const toggleNot = useCallback(() => handlers.handleToggleNot(path), [handlers, path]);

  const cond: SimpleCondition | null = data.conditionData || null;
  // Determine if it's a value-based condition and get the appropriate label and value
  const isValueCondition = cond ? 'value' in cond : false;
  const fieldLabel = isValueCondition ? 'Value' : 'Field';
  const fieldDisplay = String(isValueCondition ? cond?.value : cond?.field ?? '').split(' (')[0];
  
  // Find the operator, excluding 'field' and 'value' keys
  const operator = Object.keys(cond ?? {}).find((k) => k !== 'field' && k !== 'value') ?? 'equals';
  const rawVal = getConditionValue(cond, operator);
  const valStr = Array.isArray(rawVal) ? `[${rawVal.join(', ')}]` : String(rawVal);

  // Popover menu state for hidden actions
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); setMenuOpen(o => !o);
  }, []);
  // Ref for click-away detection
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuOpen && wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [menuOpen]);

  return (
    <div ref={wrapperRef} className="w-full h-full">
      <BaseNode
        selected={selected}
        bgClasses="from-teal-50 to-teal-100 dark:from-teal-800 dark:to-teal-900"
        ringColor="ring-teal-500"
        header="Condition"
        footer={null}
      >
        {/* Menu opener at top-right */}
        <div className="absolute top-2 right-2">
          <ActionButton onClick={toggleMenu} label="⋮" tooltip="More actions" />
        </div>
        {/* Popover with hidden actions */}
        {menuOpen && (
          <div className="absolute top-8 right-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded shadow-md p-2 flex flex-col space-y-1 z-20">
            <ActionButton onClick={toggleNot} label="🚫" tooltip="Toggle NOT" />
            <ActionButton onClick={handleEdit} label="✏️" tooltip="Edit" />
            <ActionButton onClick={handleDelete} label="🗑️" tooltip="Delete" variant="danger" />
          </div>
        )}
        {/* Main content */}
        <div
          title={`${fieldLabel}: ${fieldDisplay} ${operator} ${valStr}`}
          className="mt-1 w-full text-xs text-gray-700 dark:text-gray-200 space-y-1"
        >
          <div className="truncate text-teal-700 dark:text-teal-300">
            <span className="font-semibold text-gray-500 dark:text-gray-400 mr-1">{fieldLabel}:</span>
            {fieldDisplay}
          </div>
          <div className="truncate">{operator}</div>
          <div className="truncate text-green-700 dark:text-green-300">{valStr}</div>
        </div>
      </BaseNode>
    </div>
  );
});

ConditionNode.displayName = 'ConditionNode'; 